import type { SelectedTravelQuote } from './selectedQuote';
import { isQuoteFreshForHold } from './useFareCountdown';

export function isQuoteFromActiveSearch(
  quote: Pick<SelectedTravelQuote<unknown>, 'searchIdentity'> | null,
  activeSearchIdentity: string | undefined,
): boolean {
  return Boolean(quote && activeSearchIdentity && quote.searchIdentity === activeSearchIdentity);
}

export interface PreHoldAuthorityInput {
  quote: Pick<SelectedTravelQuote<unknown>, 'quotedAt' | 'searchIdentity'> | null;
  activeSearchIdentity: string | undefined;
  requoting?: boolean;
  scheduleChanged?: boolean;
}

export interface PreHoldSubmissionAuthority extends PreHoldAuthorityInput {
  caseId: number | null;
  /** Identidad exacta: incluye también el quote token vigente. */
  payloadIdentity: string | null;
  /** Los verticales que cotizan selección no pueden caer al precio anterior. */
  authoritativeQuoteReady: boolean;
}

/** Final side-effect guard used before attempt identity or hold mutation. */
export function canStartPreHold(input: PreHoldAuthorityInput, now: number): boolean {
  return Boolean(
    !input.requoting &&
      !input.scheduleChanged &&
      isQuoteFromActiveSearch(input.quote, input.activeSearchIdentity) &&
      input.quote &&
      // SOLO el TTL local (skew-inmune): el vencimiento FIRMADO de la oferta
      // es advisory y no entra al guard — un reloj de navegador adelantado no
      // puede bloquear una oferta que el proveedor todavía acepta. La
      // enforcement del firmado es del backend (`OFFER_EXPIRED`
      // clean-terminal, con re-cotización). Ver `fareCountdownAt`.
      isQuoteFreshForHold(input.quote.quotedAt, now),
  );
}

/**
 * Segundo guard, después de cualquier PATCH previo al hold. La promesa puede
 * devolver sobre otro render: sólo se retoma si caso, payload y autoridad viva
 * siguen siendo exactamente los que el asesor aprobó al hacer click.
 */
export function canResumePreHold(
  expected: PreHoldSubmissionAuthority,
  current: PreHoldSubmissionAuthority,
  now: number,
): boolean {
  return Boolean(
    expected.caseId &&
      current.caseId === expected.caseId &&
      expected.payloadIdentity &&
      current.payloadIdentity === expected.payloadIdentity &&
      current.quote === expected.quote &&
      current.authoritativeQuoteReady &&
      canStartPreHold(current, now),
  );
}

/**
 * Crear perfiles antes del primer hold cambia únicamente la identidad del
 * payload porque aparecen sus `travelerId`. La continuación acepta el render
 * anterior o ese payload enlazado, pero ningún tercer cambio concurrente.
 */
export function canResumePreHoldAfterTravelerLinking(
  expected: PreHoldSubmissionAuthority,
  current: PreHoldSubmissionAuthority,
  allowedPayloadIdentities: ReadonlySet<string>,
  linkedPayloadIdentity: string,
  now: number,
): boolean {
  if (
    !current.payloadIdentity ||
    !allowedPayloadIdentities.has(current.payloadIdentity)
  ) return false;

  return canResumePreHold(
    { ...expected, payloadIdentity: linkedPayloadIdentity },
    { ...current, payloadIdentity: linkedPayloadIdentity },
    now,
  );
}
